import React, { FunctionComponent, useEffect, useState } from 'react';
import { globalHistory } from '@reach/router';
import { SbBlokData, storyblokEditable } from '@storyblok/react';
import { useBreadcrumb } from 'gatsby-plugin-breadcrumb';

import { useBankProducts } from '../context/BankProductsContext';
import { generateCookies, getCrumbLabel, getCrumbName, getCrumbs, getUrlParams, setCookie } from '../utils';

import Breadcrumbs from './Breadcrumbs';
import { Global } from './Global';
import { HeroBanner } from './HeroBanner';
import { LinkProps } from './Link';
import { MediaProps } from './Media';
import { NotificationBanner } from './NotificationBanner';
import { RichText } from './RichText';
import { Section } from './Section';
import { MetadataType, SEO } from './Seo';
import { renderComponent } from './';

interface ListItem extends SbBlokData {
  text: string;
}

export interface Blok extends SbBlokData {
  title: string;
  logo: MediaProps[];
  introductionList?: ListItem[];
  introductionLink?: LinkProps[];
  introductionDescription?: string;
  headerBackground: MediaProps[];
  headerOutline: SbBlokData[];
  body: SbBlokData[];
  layout: SbBlokData;
  metadata?: MetadataType;
  isLinkSecondaryColor?: boolean;
  isTextWhite?: boolean;
  disableIndexing?: boolean;
  showBreadcrumbs?: boolean;
  customFooterContent?: string;
  canonical?: string;
}

interface PageProps {
  blok: Blok;
}

const isHomePage = (currentCrumb: string): boolean =>
  typeof window !== 'undefined' && (window.location.href.includes('editor?path=home') || currentCrumb.includes('home'));

export const Page: FunctionComponent<React.PropsWithChildren<PageProps>> = ({ blok }) => {
  const { contextProducts, setContextProducts } = useBankProducts();
  const [currentCrumb, setCurrentCrumb] = useState('');
  const isMobileApp = typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true';
  const { location } = globalHistory;
  const { crumbs } = useBreadcrumb({
    location,
    crumbLabel: getCrumbLabel(currentCrumb)
  });

  const {
    title = '',
    introductionList = [],
    introductionLink = [],
    headerBackground = [],
    introductionDescription,
    headerOutline = [],
    body = [],
    logo = [],
    layout,
    metadata,
    isLinkSecondaryColor = false,
    isTextWhite = true,
    disableIndexing,
    showBreadcrumbs,
    customFooterContent = '',
    canonical = ''
  } = blok;

  useEffect(() => {
    // if header outline is empty, means we don't have product-type tabs, hence we are sure we can reset contextProducts
    if (headerOutline.length === 0) {
      setContextProducts(null);
    }
  }, [contextProducts, headerOutline.length, setContextProducts]);

  useEffect(() => {
    if (location instanceof Object) {
      const urlParams = getUrlParams(location.href);

      isHomePage('home') && setCurrentCrumb(getCrumbName(location?.pathname));

      if (urlParams instanceof Object) {
        const cookieParams = generateCookies(urlParams, location);

        for (const { name, value, options } of cookieParams) {
          document.cookie = setCookie(name, value, options);
        }
      }
    }
  }, [currentCrumb, location]);

  return (
    <Global id={`${layout.id}`} customFooterContent={<RichText content={customFooterContent} className="text-xs" />}>
      <SEO
        title={metadata?.title || ''}
        description={metadata?.description || ''}
        disableIndexing={disableIndexing}
        canonical={canonical}
      />
      <NotificationBanner />
      {!isMobileApp && (
        <HeroBanner
          headerBackground={headerBackground}
          title={title}
          isTextWhite={isTextWhite}
          logo={logo}
          introductionList={introductionList}
          introductionLink={introductionLink}
          isLinkSecondaryColor={isLinkSecondaryColor}
          introductionDescription={introductionDescription}
        />
      )}

      <Section width="md" hasBorder={true} className="py-0" hasPaddingBottom={false} hasPaddingTop={false}>
        {!isHomePage(currentCrumb) && showBreadcrumbs && (
          <Breadcrumbs currentCrumb={currentCrumb} crumbPaths={getCrumbs(crumbs)} />
        )}
        {headerOutline.map(blok => (
          <div {...storyblokEditable(blok)} key={blok._uid}>
            {renderComponent({ blok }, blok.component)}
          </div>
        ))}
      </Section>
      <div className="relative">{body.map(blok => renderComponent({ key: blok._uid, blok }, blok.component))}</div>
    </Global>
  );
};
